/**
 * RssImportDialog — paste RSS URL → import N items as draft content for a company.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Rss } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RssImportDialog({ companies, open, onOpenChange, onImported }) {
  const [companyId, setCompanyId] = useState("");
  const [feedUrl, setFeedUrl] = useState("");
  const [max, setMax] = useState("5");
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!companyId || !feedUrl) {
      toast.info("Firma und Feed-URL erforderlich");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/rss-import`,
        { feed_url: feedUrl, company_id: companyId, max_items: parseInt(max) },
        { withCredentials: true }
      );
      toast.success(`${res.data.imported} Drafts importiert aus "${res.data.feed_title || "Feed"}"`);
      onOpenChange(false);
      setFeedUrl("");
      if (onImported) onImported();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import fehlgeschlagen");
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="rss-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Rss className="w-5 h-5 text-[#2563EB]" /> RSS-Feed importieren
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-[var(--studio-text-muted)]">Ziel-Firma *</label>
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger className="mt-1" data-testid="rss-company-select"><SelectValue placeholder="Firma waehlen" /></SelectTrigger>
              <SelectContent>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-[var(--studio-text-muted)]">RSS/Atom Feed-URL *</label>
            <Input
              value={feedUrl}
              onChange={(e) => setFeedUrl(e.target.value)}
              placeholder="https://example.com/blog/feed.xml"
              className="mt-1"
              data-testid="rss-feed-url"
            />
          </div>
          <div>
            <label className="text-xs text-[var(--studio-text-muted)]">Max. Eintraege (1-20)</label>
            <Select value={max} onValueChange={setMax}>
              <SelectTrigger className="mt-1" data-testid="rss-max-items"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[3, 5, 10, 15, 20].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button
            onClick={run}
            disabled={loading}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
            data-testid="rss-import-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Rss className="w-4 h-4 mr-2" />}
            Importieren
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
